/** @jsxImportSource @emotion/react */
import {Dialog, Icon, Popover, Spinner, SpinnerSize, Text, Tooltip} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {Box, IconButton, TablePagination} from "@mui/material";
import {LabelDisplayedRowsArgs} from "@mui/material/TablePagination/TablePagination";
import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
  Driver,
  DriverFilterInput,
  SearchableDriverSortableFields,
  SearchableSortDirection,
  useCreateManifestMutation,
  useSearchDriversEnhancedQuery,
  useSearchVehicleTypesQuery,
  useSetUserPrefMutation,
  VehicleType
} from "../../../generated/graphql";
import {AssignmentViewActionTypes, ManifestFilterTypePayload} from "../../../providers/AssignmentViewReducer";
import {
  COLORS,
  Constants,
  DateFormats,
  DEFAULT_POPOVER_MODIFIERS,
  ERR_WARNING_MESSAGES,
  TenantPreferences,
  UserPreferences
} from "../../../constants/constants";
import MultiSelectDropDown, {MultiSelectDropDownItem} from "../../common/MultiSelectDropDown";
import {useManifestDataContext} from "../providers/ManifestDataProvider";
import "./styles/ManifestDriverPanel.css";
import ManifestMenu from "./components/ManifestMenu";
import ManifestViewSelector from "./components/ManifestViewSelector";

import {DateRange} from "@blueprintjs/datetime";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import {format} from "date-fns";
import {isArray, isEqual} from "lodash";
import {useFeatureFlagContext} from "../../../providers/FeatureFlagProvider";
import {
  createJsonPref,
  extractJsonPref,
  extractSimplePref,
  usePreferenceContext
} from "../../../providers/PreferenceProvider";
import {getArrayFromField} from "../../../utils/General";
import {TManifestDetailsDrawerOpen} from "../../../views/UnassignedJobsView";
import {useDispatchGroupContext} from "../../../providers/DispatchGroupProvider";

import Search from "../../common/Search";
import {ManifestFilterType} from "./components/manifest-filter/ManifestFilterState";
import {ManifestWarningMessage} from "./components/ManifestWarningMessage";
import {useSuggestedAssignmentContext} from "../../../providers/SuggestedAssignmentProvider";
import {WarningIcon} from "../common/components/cards/common/icons/StatusIconV3";
import ManifestCardConfigurationV2 from "../card-configuration/v2/ManifestCardConfiguration";
import CreateManifestModal, {TDriverItem} from "../create-manifest/CreateManifestModal";
import {DateSelection, OptimizedStatusTypes, StatusType} from "./components/manifest-filter/ManifestFilter";
import ManifestFilterDialog from "./components/manifest-filter/ManifestFilterDialog";
import {StopStatusCategory} from "./components/manifest-filter/types/StopStatusManifestFilter";
import {StyledButton, StyledSelectButton} from "../../common/StyledButtons";
import {useMapVisibilityDispatch} from "../../map/MapVisibilityContext";
import {useCreateManifestDispatch, useCreateManifestState} from "../../../providers/CreateManifestProvider";
import {useAssignmentState} from "../../../providers/AssignmentProdiver";
import {useAssignmentViewDispatch, useAssignmentViewState} from "../../../providers/AssignmentViewProvider";
import {shouldShowColorSortWarning} from "./utils/colorSort.utils";
import {useManifestDetailsState} from "../../../providers/ManifestDetailsProvider";
import {useDispatchDataContext} from "../../../providers/DispatchDataProvider";
import {buildEnhancedDrivers} from "../../../utils/DriverUtils";
import {hasSelectedDispatchStations, useDispatchStationContext} from "../../../providers/DispatchStationProvider";

const ManifestDriverViewsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #ededed;
  z-index: 1;
  box-shadow: 2px 0px 12px 0px #00000026 inset;
`;

const ToolbarContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  gap: 8px;
  padding: 16px;
`;

const SuggestedBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 16px;
  padding: 8px 12px;
  background: ${COLORS.BG_DISABLED};
  border-radius: 6px;
  font-size: 12px;
  color: ${COLORS.TEXT_BLACK};
`;

const ClearSuggestionLink = styled.button`
  margin-left: auto;
  background: none;
  border: none;
  color: ${COLORS.BLUE};
  cursor: pointer;
  font-size: 12px;
  text-decoration: none;
  padding: 0;

  &:hover {
    color: ${COLORS.BLUE_DARK};
  }
`;

const LeftToolbar = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const RightToolbar = styled.div<{isNarrowed?: boolean}>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${(props) => (props.isNarrowed ? "0" : "5px")};
`;

const PagingMenuContainer = styled.div({
  marginTop: "auto",
  display: "flex",
  justifyContent: "flex-end",
  height: "auto"
});

const MuiPagingMenu = styled(TablePagination)`
  height: auto;
  margin-right: 18px;
  font-size: 13px;
  button.MuiButtonBase-root:not(.MuiMenuItem-root) {
    padding: 0;
    margin-left: -6px;
  }

  button.MuiButtonBase-root:nth-of-type(2) {
    margin-left: -8px;
    margin-right: -7px;
  }
  button.MuiButtonBase-root:nth-of-type(3) {
    margin-left: -8px;
  }
  button.MuiButtonBase-root:nth-of-type(4) {
    margin-left: -8px;
  }
  button.MuiButtonBase-root > b {
    margin-left: -2px;
    background: red;
  }
`;

const CreateManifestDialog = styled(Dialog)`
  width: fit-content;
  height: fit-content;
`;

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const {count, page, rowsPerPage, onPageChange} = props;

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    onPageChange(event as React.MouseEvent<HTMLButtonElement>, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{flexShrink: 0, ml: 2.5, fontSize: "13px"}}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        <FirstPageIcon fontSize={"small"} />
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        <KeyboardArrowLeft />
      </IconButton>
      Page <b>{page + 1}</b> of <b>{Math.max(1, Math.ceil(count / rowsPerPage))}</b>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPageIcon fontSize={"small"} />
      </IconButton>
    </Box>
  );
}

enum ManifestWarningMessageTypes {
  HAS_DRIVER_REQUIREMENTS_FILTER = "Showing Qualified Drivers",
  SORT_WARNING = "Color sorting is limited to the current page"
}

enum ManifestWarningTooltipMessages {
  HAS_DRIVER_REQUIREMENTS_FILTER = "Unselect Qualified Drivers if you’d like to see all drivers"
}

export type SelectedFilterState = {
  filterType: ManifestFilterType;
  filterValue: string | number | undefined;
};
export type ManifestFilterPreference = {
  statusTypes?: StatusType[];
  optimizedStatusTypes?: OptimizedStatusTypes[];
  dateSelectionText?: string;
  emptyActive?: boolean;
  newActive?: boolean;
};
type ManifestDriverProps = {
  driverGroupFilters?: Set<number>;
  onManifestDetailsDrawerOpen: TManifestDetailsDrawerOpen;
  hideCreateManifestButton?: boolean;
  filterSelected(filterState: ManifestFilterTypePayload[], oldFilterState?: ManifestFilterTypePayload[]): void;
  filterDateChanged(dateRange: DateRange | undefined, selectedDateRange: DateRange | undefined): void;
  selectDriverGroup(key: number, arrayOfManifestFilter: React.MutableRefObject<number[]>): void;
  unselectDriverGroup(key: number, arrayOfManifestFilter: React.MutableRefObject<number[]>): void;
  replaceDriverGroup(arrayOfManifestFilter: React.MutableRefObject<number[]>): void;
  unselectAllDriverGroup(arrayOfManifestFilter: React.MutableRefObject<number[]>): void;
  handleManifestQuery(query: string): void;
  disabledManifestIds?: number[];
  setStatusTypes: React.Dispatch<React.SetStateAction<StatusType[]>>;
  statusTypes: StatusType[];
  setSelectedOptimizedStatusTypes: React.Dispatch<React.SetStateAction<OptimizedStatusTypes[]>>;
  selectedOptimizedStatusTypes: OptimizedStatusTypes[];
  /** When true, filter/sort/page changes do NOT persist to user preferences. */
  skipSavePreferences?: boolean;
};
const fetchDriverLimit = 100;

const FilterDefaultText = "Filters";

const ManifestDriverPanel = ({
  onManifestDetailsDrawerOpen,
  hideCreateManifestButton = false,
  filterSelected,
  filterDateChanged,
  selectDriverGroup,
  unselectDriverGroup,
  replaceDriverGroup,
  unselectAllDriverGroup,
  handleManifestQuery,
  disabledManifestIds,
  setStatusTypes,
  statusTypes,
  selectedOptimizedStatusTypes,
  setSelectedOptimizedStatusTypes,
  skipSavePreferences = false
}: ManifestDriverProps) => {
  const {
    colorSort: colorSortEnable,
    manifestPageSizes,
    backendColorRules: backendColorRulesEnabled
  } = useFeatureFlagContext();
  const {userPreferences, userPrefsQueryRefetch, isUserPreferencesLoaded, tenantPreferences} = usePreferenceContext();

  const suggestedAssignmentState = useSuggestedAssignmentContext();
  const {jobsRequirements, reassignJobsRequirements, selectedManifest} = useAssignmentState();
  const assignmentViewState = useAssignmentViewState();
  const manifestDetailsState = useManifestDetailsState();
  const assignmentViewDispatch = useAssignmentViewDispatch();

  const createManifestState = useCreateManifestState();
  const createManifestStateDispatch = useCreateManifestDispatch();
  const mapVisibilityDispatch = useMapVisibilityDispatch();
  const {dispatchGroups} = useDispatchDataContext();
  const {driverGroups} = useDispatchGroupContext();
  const manifestDataState = useManifestDataContext();
  const dispatchStationState = useDispatchStationContext();

  const [isOpenManifestModal, setIsOpenManifestModal] = useState<boolean>(false);
  const [isFilterManifestModalOpen, setFilterManifestModalOpen] = useState<boolean>(false);
  const [createManifest] = useCreateManifestMutation();
  const [filterButtonLabel, setFilterButtonLabel] = useState<string>(FilterDefaultText);
  const [selectedFilters, setSelectedFilters] = useState<ManifestFilterTypePayload[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | undefined>();
  const [searchExpanded, setSearchExpanded] = useState<boolean>(false);
  const [dateSelectionText, setDateSelectionText] = useState<string>(DateSelection.All_DAY);
  const [isEmptyActived, setIsEmptyActived] = useState<boolean>(false);
  const [isNewActived, setIsNewActived] = useState<boolean>(false);

  const selectedDriverGroupIds = useRef<number[]>([]);
  const [initializing, setInitializing] = useState<boolean>(true);
  const [driverItems, setDriverItems] = useState<Driver[]>([]);
  const [driverQuery, setDriverQuery] = useState<string>("");
  const [driverFilter, setDriverFilter] = useState<DriverFilterInput>({});

  const [setUserPref] = useSetUserPrefMutation();
  const vehicleTypesResults = useSearchVehicleTypesQuery({
    variables: {
      limit: 1000
    }
  });

  const {fetchMore, refetch} = useSearchDriversEnhancedQuery({
    variables: {
      limit: fetchDriverLimit,
      sort: [
        {
          field: SearchableDriverSortableFields.Name,
          direction: SearchableSortDirection.Asc
        }
      ],
      filter: driverFilter,
      offset: 0
    },
    onCompleted: (data) => {
      setDriverItems(data?.searchDriversEnhanced?.items as Driver[]);
    }
  });

  const canCreateManifestOutSide = extractSimplePref(
    tenantPreferences,
    TenantPreferences.createManifestOutSide,
    false
  ).value;

  const currentManifestFilterHeader = useMemo(
    () => extractJsonPref(userPreferences, UserPreferences.manifestFilterHeader, {})?.value as ManifestFilterPreference,
    [userPreferences]
  );

  const isAssignPanel = useMemo(() => {
    return manifestDetailsState.manifestDriverId !== undefined;
  }, [manifestDetailsState.manifestDriverId]);
  const shouldSkipSavePref = skipSavePreferences;

  const driverGroupItems = useMemo(() => {
    const map: Map<number, MultiSelectDropDownItem> = new Map();
    if (driverGroups.length > 0) {
      const groups = [...driverGroups];
      groups.sort((a, b) => a.dispatchGroup!.localeCompare(b.dispatchGroup!));
      groups.forEach((group) => {
        map.set(group.dispatchGroupId!, {
          name: group.dispatchGroup,
          selected: assignmentViewState.manifestFilters?.hasFilterValue(
            group.dispatchGroupId!,
            ManifestFilterType.DRIVER_GROUP
          ), //Maybe save as user pref
          id: group.dispatchGroupId
        } as MultiSelectDropDownItem);
      });
    }
    return map;
  }, [driverGroups, assignmentViewState.manifestFilters]);

  const filteredDispatchGroupIds = useMemo(() => {
    let dipatchGroupIds = [];
    if (assignmentViewState.manifestFilters?.hasFilter(ManifestFilterType.DRIVER_GROUP)) {
      dipatchGroupIds = Array.from(
        assignmentViewState.manifestFilters.manifestFilters.get(ManifestFilterType.DRIVER_GROUP)?.value ?? []
      );
    } else if (hasSelectedDispatchStations(dispatchStationState)) {
      dipatchGroupIds = driverGroups.map((item) => item.dispatchGroupId);
    }
    return dipatchGroupIds.map((id) => ({dispatchGroupId: {eq: id}}));
  }, [assignmentViewState.manifestFilters, dispatchStationState, driverGroups]);

  // A selected station with no driver groups must show NO drivers (not all).
  // Only for the station branch — not the user DRIVER_GROUP filter, and not "no station".
  const stationHasNoDriverGroups = useMemo(
    () =>
      !assignmentViewState.manifestFilters?.hasFilter(ManifestFilterType.DRIVER_GROUP) &&
      hasSelectedDispatchStations(dispatchStationState) &&
      driverGroups.length === 0,
    [assignmentViewState.manifestFilters, dispatchStationState, driverGroups]
  );

  const showWarningMessage = useMemo(
    () =>
      shouldShowColorSortWarning({
        backendColorRulesEnabled,
        sortByColorEnabled: assignmentViewState.sortByColor.enable,
        pageCount: manifestDataState.pageCount
      }),
    [backendColorRulesEnabled, manifestDataState.pageCount, assignmentViewState.sortByColor.enable]
  );

  const displayData = useMemo(() => {
    if (suggestedAssignmentState.isLoading) return [];
    if (suggestedAssignmentState.state === "complete" && suggestedAssignmentState.candidates.length === 0) return [];
    if (suggestedAssignmentState.isActive) {
      const candidateIds = new Set(suggestedAssignmentState.candidates.map((c) => c.manifestDriverId));
      return (manifestDataState.data ?? []).filter((m) => candidateIds.has(m.manifestDriverId));
    }
    return manifestDataState.data ?? [];
  }, [
    suggestedAssignmentState.isLoading,
    suggestedAssignmentState.isActive,
    suggestedAssignmentState.state,
    suggestedAssignmentState.candidates,
    manifestDataState.data
  ]);

  const onLoadMore = useCallback(() => {
    const currentLength = driverItems.length;
    fetchMore({
      variables: {
        offset: currentLength
      }
    }).then((result) => {
      if (result) {
        setDriverItems((prev) => [...prev, ...(result.data?.searchDriversEnhanced?.items as Driver[])]);
      }
    });
  }, [driverItems.length, fetchMore]);

  const handleDateRangeChanged = useCallback((dateRange: DateRange | undefined) => {
    setSelectedDateRange(dateRange);
  }, []);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | React.ChangeEvent<unknown> | null,
    page: number
  ) => {
    manifestDataState.setPage(page);
    mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (manifestDataState.setPageSize) {
      manifestDataState.setPageSize(parseInt(event.target.value));
      mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
      if (shouldSkipSavePref) return;
      setUserPref({
        variables: {
          name: UserPreferences.manifestRowPerPage,
          input: createJsonPref(parseInt(event.target.value), true)
        }
      }).then(() => {
        userPrefsQueryRefetch?.();
      });
    }
  };

  const onSelectDriverGroup = (key: number) => {
    selectedDriverGroupIds.current.push(key);
    selectDriverGroup(key, selectedDriverGroupIds);
    mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
  };

  const onUnselectDriverGroup = (key: number) => {
    selectedDriverGroupIds.current = selectedDriverGroupIds.current.filter((d) => d !== key);
    unselectDriverGroup(key, selectedDriverGroupIds);
    mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
  };

  const onUnselectAllDriverGroup = useCallback(() => {
    selectedDriverGroupIds.current = [];
    unselectAllDriverGroup(selectedDriverGroupIds);
    mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
  }, [mapVisibilityDispatch, unselectAllDriverGroup]);

  const handleRowLabels = (paginationInfo: LabelDisplayedRowsArgs): React.ReactNode => {
    return (
      <Text style={{fontSize: "13px"}}>
        <b>{paginationInfo.from}</b> to <b>{paginationInfo.to}</b> of <b>{paginationInfo.count}</b>
      </Text>
    );
  };

  const handleCreateManifest = (date: Date) => {
    createManifestStateDispatch({
      type: "CREATE_MANIFEST",
      payload: {
        date: format(date, DateFormats.localDate),
        mutate: createManifest
      }
    });
    setIsOpenManifestModal(false);
  };

  const openCreateManifestModal = () => {
    setIsOpenManifestModal(true);
  };

  const closeCreateManifestModal = () => {
    createManifestStateDispatch({
      type: "CLEAR_SELECTED_CREATE_MANIFESTS"
    });
    setIsOpenManifestModal(false);
  };

  const openFilterManifestModal = () => {
    setFilterManifestModalOpen(true);
  };

  const updateManifestFilterHeaderPref = useCallback(
    (newPref: ManifestFilterPreference) => {
      const newManifestFilterPref: ManifestFilterPreference = {
        ...currentManifestFilterHeader,
        ...newPref
      };
      if (shouldSkipSavePref || isEqual(newManifestFilterPref, currentManifestFilterHeader)) return;
      setUserPref({
        variables: {
          name: UserPreferences.manifestFilterHeader,
          input: createJsonPref(newManifestFilterPref, true)
        }
      }).then(() => {
        userPrefsQueryRefetch?.();
      });
    },
    [currentManifestFilterHeader, shouldSkipSavePref, setUserPref, userPrefsQueryRefetch]
  );

  const closeFilterManifestModal = useCallback(() => {
    setFilterManifestModalOpen(false);
    const newFilter: ManifestFilterPreference = {
      dateSelectionText: dateSelectionText,
      emptyActive: isEmptyActived,
      newActive: isNewActived,
      statusTypes: statusTypes,
      optimizedStatusTypes: selectedOptimizedStatusTypes
    };
    updateManifestFilterHeaderPref(newFilter);
  }, [
    dateSelectionText,
    isEmptyActived,
    isNewActived,
    selectedOptimizedStatusTypes,
    statusTypes,
    updateManifestFilterHeaderPref
  ]);

  const handleSelect = (item: TDriverItem) => {
    createManifestStateDispatch({type: "SET_SELECTED_CREATE_MANIFEST", payload: item});
  };

  const handleClear = () => {
    createManifestStateDispatch({
      type: "CLEAR_SELECTED_CREATE_MANIFESTS"
    });
    refetch();
  };

  const onFilterSelect = (
    filterType: ManifestFilterType.NONE | ManifestFilterType.MANIFEST_AGE | ManifestFilterType.STOP_STATUS,
    filterValue: string | number | undefined
  ) => {
    const newManifestFilter = {
      filterType,
      value: filterValue
    };
    if (filterType === ManifestFilterType.NONE) {
      filterSelected([]);
      setSelectedFilters([]);
    } else {
      const updatedFilters = selectedFilters.some((x) => x.filterType === filterType)
        ? selectedFilters.filter((x) => x.filterType !== filterType)
        : [...selectedFilters, newManifestFilter];
      setSelectedFilters(updatedFilters);
      filterSelected(updatedFilters, selectedFilters);
    }
    mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
  };

  const onFilterDateChanged = (dateRange: DateRange | undefined) => {
    handleDateRangeChanged(dateRange);
    filterDateChanged(dateRange, selectedDateRange);
    mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
  };

  const clearAllFilters = useCallback(() => {
    //Empty && New
    setSelectedFilters([]);
    setIsEmptyActived(false);
    setIsNewActived(false);
    filterSelected([], selectedFilters);

    //SelectedDateRange
    handleDateRangeChanged(undefined);
    setDateSelectionText(DateSelection.All_DAY);
    filterDateChanged(undefined, selectedDateRange);

    //SelectedManifestStatusTypes
    setStatusTypes([]);

    //Clear selected Optimized Status Types
    setSelectedOptimizedStatusTypes([]);

    //Save UserPref
    if (shouldSkipSavePref) return;
    setUserPref({
      variables: {
        name: UserPreferences.manifestDateRangeHeader,
        input: createJsonPref(undefined, true)
      }
    });
    const resetFilter: ManifestFilterPreference = {
      dateSelectionText: "",
      emptyActive: false,
      newActive: false
    };
    updateManifestFilterHeaderPref(resetFilter);
  }, [
    filterSelected,
    selectedFilters,
    handleDateRangeChanged,
    filterDateChanged,
    selectedDateRange,
    setStatusTypes,
    setSelectedOptimizedStatusTypes,
    shouldSkipSavePref,
    setUserPref,
    updateManifestFilterHeaderPref
  ]);

  const onSearchManifest = React.useCallback(
    (query: string) => {
      mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
      handleManifestQuery(query);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    //Close Manifest Details refetch manifest data by userPref
    if (!isAssignPanel && !skipSavePreferences) {
      setInitializing(true);
    }
  }, [isAssignPanel, skipSavePreferences]);

  // Load user preferences on initial render
  useEffect(() => {
    if (isUserPreferencesLoaded && initializing && manifestDataState.setPageSize) {
      const manifestRowPerPage = extractJsonPref(userPreferences, UserPreferences.manifestRowPerPage)?.value;
      if (manifestRowPerPage) {
        manifestDataState.setPageSize(parseInt(manifestRowPerPage));
      }
      const manifestDateRangeHeader = extractJsonPref(userPreferences, UserPreferences.manifestDateRangeHeader)?.value;
      if (manifestDateRangeHeader && isArray(manifestDateRangeHeader) && manifestDateRangeHeader.length > 1) {
        const dateRange = manifestDateRangeHeader.map((d) => new Date(d));
        handleDateRangeChanged(dateRange as DateRange);
        assignmentViewDispatch({
          type: AssignmentViewActionTypes.REPLACE_MANIFEST_FILTER_VALUE,
          payload: {filterType: ManifestFilterType.DATE_RANGE, value: dateRange as DateRange}
        });
      }
      const savedDriverGroupIds = extractJsonPref(userPreferences, UserPreferences.manifestGroupHeader)?.value;
      if (savedDriverGroupIds && isArray(savedDriverGroupIds)) {
        selectedDriverGroupIds.current = savedDriverGroupIds;
        savedDriverGroupIds.forEach((key) => {
          assignmentViewDispatch({
            type: AssignmentViewActionTypes.ADD_MANIFEST_FILTER,
            payload: {filterType: ManifestFilterType.DRIVER_GROUP, value: key}
          });
        });
      }
      const manifestFilterHeader = extractJsonPref(userPreferences, UserPreferences.manifestFilterHeader)
        ?.value as ManifestFilterPreference;
      if (Object.keys(manifestFilterHeader ?? {}).length > 0) {
        const isEmptyActived = manifestFilterHeader.emptyActive ?? false;
        setIsEmptyActived(isEmptyActived);
        const isNewActived = manifestFilterHeader.newActive ?? false;
        setIsNewActived(isNewActived);
        if (!isEmptyActived && !isNewActived) {
          setSelectedFilters([]);
          filterSelected([]);
        } else {
          const filterState: ManifestFilterTypePayload[] = [];
          if (isEmptyActived) {
            filterState.push({filterType: ManifestFilterType.STOP_STATUS, value: StopStatusCategory.COMPLETE});
          }
          if (isNewActived) {
            filterState.push({filterType: ManifestFilterType.MANIFEST_AGE, value: Constants.MANIFEST_NEW_AGE});
          }
          filterSelected(filterState);
          setSelectedFilters(filterState);
        }
        const dateSelection = manifestFilterHeader.dateSelectionText ?? "";
        setDateSelectionText(dateSelection);
        const statusTypes = manifestFilterHeader.statusTypes ?? [];
        setStatusTypes(statusTypes);
        const optimizedStatusTypes = manifestFilterHeader.optimizedStatusTypes ?? [];
        setSelectedOptimizedStatusTypes(optimizedStatusTypes);
      }
      const manifestSortingHeader = extractJsonPref(userPreferences, UserPreferences.manifestSortingHeader)?.value;
      if (manifestSortingHeader && Object.keys(manifestSortingHeader).length > 0) {
        assignmentViewDispatch({
          type: AssignmentViewActionTypes.SET_MANIFEST_SORT,
          payload: manifestSortingHeader
        });
      }
      if (colorSortEnable) {
        const manifestCardSortByColor = extractJsonPref(
          userPreferences,
          UserPreferences.manifestCardSortByColor
        )?.value;
        if (manifestCardSortByColor !== undefined) {
          assignmentViewDispatch({
            type: AssignmentViewActionTypes.SET_SORT_BY_COLOR,
            payload: manifestCardSortByColor
          });
        }
      }
      setInitializing(false);
    }
  }, [
    userPreferences,
    initializing,
    manifestDataState,
    assignmentViewDispatch,
    assignmentViewState.manifestSort,
    colorSortEnable,
    filterSelected,
    setStatusTypes,
    isAssignPanel,
    handleDateRangeChanged,
    manifestPageSizes,
    setSelectedOptimizedStatusTypes,
    isUserPreferencesLoaded
  ]);

  // Update driver filter when driverQuery or filterDispatchGroupIds change
  useEffect(() => {
    setDriverFilter({
      driver: {
        driverStatus: {eq: "A"},
        ...(canCreateManifestOutSide
          ? {}
          : stationHasNoDriverGroups
          ? {dispatchGroupId: {eq: -1}}
          : {or: filteredDispatchGroupIds})
      },
      ...(driverQuery.length > 0 && {
        multiFieldWildcard: {
          wildcard: `*${driverQuery}*`,
          caseInsensitive: true,
          fields: ["name.keyword", "driverCode.keyword"]
        }
      })
    });
  }, [driverQuery, filteredDispatchGroupIds, stationHasNoDriverGroups, canCreateManifestOutSide]);

  // Update filter button label based on selected filters
  useEffect(() => {
    let numberOfFilters = 0;
    if (isNewActived) {
      numberOfFilters++;
    }
    if (isEmptyActived) {
      numberOfFilters++;
    }
    if (selectedDateRange) {
      numberOfFilters++;
    }
    if (statusTypes.length > 0) {
      numberOfFilters += statusTypes.length;
    }
    if (selectedOptimizedStatusTypes.length > 0) {
      numberOfFilters += selectedOptimizedStatusTypes.length;
    }
    setFilterButtonLabel(numberOfFilters > 0 ? `${numberOfFilters} Filters` : FilterDefaultText);
  }, [isEmptyActived, isNewActived, selectedDateRange, selectedOptimizedStatusTypes.length, statusTypes]);

  // Update selected driver ids when driver group items change
  useEffect(() => {
    if (initializing || driverGroupItems.size === 0) return;
    const updatedSelectedDriverGroupIds: number[] = [];
    selectedDriverGroupIds.current.forEach((id) => {
      if (driverGroupItems.has(id)) {
        updatedSelectedDriverGroupIds.push(id);
      }
    });
    if (updatedSelectedDriverGroupIds.length !== selectedDriverGroupIds.current.length) {
      selectedDriverGroupIds.current = updatedSelectedDriverGroupIds;
      replaceDriverGroup(selectedDriverGroupIds);
    }
  }, [driverGroupItems, initializing, replaceDriverGroup, onUnselectAllDriverGroup]);

  return (
    <ManifestDriverViewsContainer data-testid="manifestDriverPanel">
      <ToolbarContainer>
        <LeftToolbar>
          {!hideCreateManifestButton && (
            <>
              <StyledButton
                data-testid="toolbar-create-button"
                disabled={isOpenManifestModal}
                intent="primary"
                endIcon={"plus"}
                onClick={openCreateManifestModal}
              />
              <CreateManifestDialog
                isOpen={isOpenManifestModal}
                portalClassName="create-manifest-dialog"
                onClose={closeCreateManifestModal}
              >
                <CreateManifestModal
                  onLoadMore={onLoadMore}
                  vehicleTypes={
                    getArrayFromField(vehicleTypesResults?.data?.searchVehicleTypes?.items) as VehicleType[]
                  }
                  options={buildEnhancedDrivers(
                    driverItems,
                    dispatchGroups.filter((dg) => dg.dispatchType === "D")
                  )}
                  selectedOptions={createManifestState.selectedDrivers}
                  onSearchDriver={setDriverQuery}
                  onOptionSelect={handleSelect}
                  onCreateManifest={handleCreateManifest}
                  onClear={handleClear}
                  onClose={closeCreateManifestModal}
                />
              </CreateManifestDialog>
            </>
          )}
          <Popover
            portalClassName="filter-manifest-portal"
            popoverClassName="filter-manifest-modal"
            isOpen={isFilterManifestModalOpen}
            onClose={closeFilterManifestModal}
            content={
              <ManifestFilterDialog
                selectedDateRange={selectedDateRange}
                onChangeDateRange={onFilterDateChanged}
                dateSelectionText={dateSelectionText}
                setDateSelectionText={setDateSelectionText}
                selectedStatusTypes={statusTypes}
                setSelectedStatusTypes={setStatusTypes}
                selectedOptimizedStatusTypes={selectedOptimizedStatusTypes}
                setSelectedOptimizedStatusTypes={setSelectedOptimizedStatusTypes}
                onFilterSelect={onFilterSelect}
                isEmptyActived={isEmptyActived}
                setIsEmptyActived={setIsEmptyActived}
                isNewActived={isNewActived}
                setIsNewActived={setIsNewActived}
                onClose={closeFilterManifestModal}
              />
            }
            minimal
            modifiers={DEFAULT_POPOVER_MODIFIERS}
          >
            <StyledSelectButton
              data-testid="toolbar-filter-button"
              hasValue={filterButtonLabel !== FilterDefaultText}
              text={filterButtonLabel}
              endIcon={
                filterButtonLabel !== FilterDefaultText ? (
                  <Icon data-testid="clear-icon" icon="cross" onClick={clearAllFilters} />
                ) : (
                  "chevron-down"
                )
              }
              onClick={openFilterManifestModal}
            />
          </Popover>
          <MultiSelectDropDown
            text={"Groups"}
            testIdPrefix={"driver-groups"}
            items={driverGroupItems}
            onSelected={onSelectDriverGroup}
            onUnselected={onUnselectDriverGroup}
            clearFilter
            onUnselectedAll={onUnselectAllDriverGroup}
          />
        </LeftToolbar>
        <RightToolbar isNarrowed={manifestDataState.hasBadData}>
          {manifestDataState.hasBadData && (
            <Tooltip content={ERR_WARNING_MESSAGES.BAD_DATA} placement={"auto"}>
              <div style={{width: "15px", height: "15px"}}>
                <WarningIcon />
              </div>
            </Tooltip>
          )}
          <Search
            minWidth={manifestDataState.hasBadData ? "65px" : undefined}
            idPrefix={"manifests"}
            onSearch={onSearchManifest}
            isConstrainedByFilters={false}
            onOpenChanged={setSearchExpanded}
            minimal
          />
          {!searchExpanded && <ManifestCardConfigurationV2 />}
          <ManifestMenu
            dispatch={assignmentViewDispatch}
            manifestSort={assignmentViewState.manifestSort}
            sortByColor={assignmentViewState.sortByColor}
          />
        </RightToolbar>
      </ToolbarContainer>
      {showWarningMessage && <ManifestWarningMessage content={ManifestWarningMessageTypes.SORT_WARNING} />}
      {(isAssignPanel ? reassignJobsRequirements.size > 0 : jobsRequirements.size > 0) && (
        <ManifestWarningMessage
          content={ManifestWarningMessageTypes.HAS_DRIVER_REQUIREMENTS_FILTER}
          tooltipContent={ManifestWarningTooltipMessages.HAS_DRIVER_REQUIREMENTS_FILTER}
        />
      )}
      {suggestedAssignmentState.isActive && (
        <SuggestedBanner data-testid="suggested-assignment-banner">
          <Icon icon="info-sign" size={14} color={COLORS.TEXT_GRAY_MEDIUM} />
          <span>You&apos;re currently seeing Suggested Drivers, sorted by least added time.</span>
          <ClearSuggestionLink onClick={suggestedAssignmentState.cancel}>Clear suggestion</ClearSuggestionLink>
        </SuggestedBanner>
      )}
      {suggestedAssignmentState.isLoading && (
        <SuggestedBanner data-testid="suggested-assignment-loading">
          <Icon icon="info-sign" size={14} color={COLORS.TEXT_GRAY_MEDIUM} />
          <span>
            Finding the best drivers for this job with your current driver filters. This may take a moment depending on
            the number of available drivers.
          </span>
          <Spinner size={SpinnerSize.SMALL} style={{marginLeft: "auto"}} />
        </SuggestedBanner>
      )}
      {suggestedAssignmentState.state === "complete" && suggestedAssignmentState.candidates.length === 0 && (
        <SuggestedBanner data-testid="suggested-assignment-empty">
          <Icon icon="info-sign" size={14} color={COLORS.TEXT_GRAY_MEDIUM} />
          <span>No matching drivers found for this job with your current driver filters.</span>
          <ClearSuggestionLink onClick={suggestedAssignmentState.cancel}>Dismiss</ClearSuggestionLink>
        </SuggestedBanner>
      )}
      <ManifestViewSelector
        sortByColor={assignmentViewState.sortByColor}
        isManifestDetailsDrawerOpen={manifestDetailsState.manifestDriverId !== undefined}
        selectedManifest={selectedManifest}
        disabledManifestIds={disabledManifestIds ?? []}
        data={displayData}
        onManifestDetailsDrawerOpen={onManifestDetailsDrawerOpen}
      />
      <PagingMenuContainer>
        <MuiPagingMenu
          count={manifestDataState.total}
          page={manifestDataState.page}
          rowsPerPage={manifestDataState.pageSize}
          rowsPerPageOptions={manifestPageSizes}
          showFirstButton
          showLastButton
          labelDisplayedRows={handleRowLabels}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          size="small"
        />
      </PagingMenuContainer>
    </ManifestDriverViewsContainer>
  );
};

export default ManifestDriverPanel;
